import {
  Button,
  Icon,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AnalyticsEventName, trackAmplitudeEvent } from '@schooly/analytics';
import { SchoolProperty, SchoolPropertyCategory } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useFlag } from '@schooly/hooks/use-flag';
import { usePrevious } from '@schooly/hooks/use-previous';
import {
  DeleteIcon,
  DragIcon,
  InformationIcon,
  LeavingReasonIcon,
  PlusIcon,
  RollBackIcon,
  SettingsIcon,
} from '@schooly/style';

import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable, OnDragEndResponder } from 'react-beautiful-dnd';
import { UseFieldArrayReturn, useFormContext } from 'react-hook-form-lts';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';

import { ControlTextField } from '../../../../../components/uikit-components/FormTextField/ControlTextField';
import { SchoolGeneralRowAction } from '../../../SchoolGeneral/SchoolGeneralRowAction';
import { SchoolGeneralStatusesReplaceModal } from '../../../SchoolGeneral/SchoolGeneralStatuses/SchoolGeneralStatusesReplaceModal';
import {
  SchoolTuneStatusesArchivable,
  SchoolTuneStatusesForm,
  SchoolTuneStatusModalMode,
} from './scheme';
import { getCategoryTypeFromName, getEmptyStatus, sortByOrder } from './utils';

const FORMER_STATUS_TOOLTIP_TIMEOUT = 5000;

export interface SchoolTuneStatusesModalCategoryProps extends SchoolTuneStatusesArchivable {
  type: keyof SchoolTuneStatusesForm;
  category: SchoolPropertyCategory;
  title: string;
  showReEnrollmentStatuses?: boolean;
  leavingStatusId?: string;
  formFieldArray: UseFieldArrayReturn<SchoolTuneStatusesForm, SchoolTuneStatusModalMode, 'id'>;
}

export const SchoolTuneStatusesModalCategory: FC<SchoolTuneStatusesModalCategoryProps> = ({
  type,
  category,
  title,
  showArchived,
  setShowArchived,
  autoSwitchedArchivedOn,
  showReEnrollmentStatuses,
  leavingStatusId,
  formFieldArray,
}) => {
  const { formatMessage } = useIntl();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const fieldName = type;
  const showTitle = !!category.parent_category_id;

  const [isFormerStatusTooltipVisible, showFormerStatusTooltip, hideFormerStatusTooltip] =
    useFlag();
  const { getValues, control } = useFormContext<SchoolTuneStatusesForm>();

  const tooltipPopper = useRef<HTMLDivElement | null>(null);
  const { fields, append, remove, replace, move, update } = formFieldArray;

  const [archivingFieldIndex, setArchivingFieldIndex] = useState<number>();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  useEffect(() => {
    showFormerStatusTooltip();

    setTimeout(() => {
      hideFormerStatusTooltip();
    }, FORMER_STATUS_TOOLTIP_TIMEOUT);
  }, []);

  // (TR-4607): Have to spread active & archived fields to two different lists and
  // render/manage them separately.
  const [activeFields, archivedFields] = useMemo(
    () =>
      fields.reduce<Array<Array<{ index: number; field: (typeof fields)[number] }>>>(
        (prev, field, index) => {
          if (!showReEnrollmentStatuses && field.source?.type === 're_enrollment') {
            return prev;
          }
          if (field.category?.name !== category.name) {
            return prev;
          }
          prev[field.archived ? 1 : 0].push({ index, field });
          return prev;
        },
        [[], []],
      ),
    [category, fields, showReEnrollmentStatuses],
  );

  const prevActiveFields = usePrevious(activeFields);

  const firstActiveField = getValues(`${fieldName}.${activeFields[0]?.index}`);

  const shouldFocusLastItem = Boolean(
    activeFields.length === 1
      ? !firstActiveField.name
      : prevActiveFields && activeFields && activeFields.length - prevActiveFields.length === 1,
  );

  const handleCloseReplaceModal = useCallback(() => {
    setArchivingFieldIndex(undefined);
  }, []);

  const addStatus = useCallback(() => {
    append(getEmptyStatus(category));
  }, [append, category]);

  const archiveStatus = useCallback(
    (index: number, replaceId: SchoolProperty['id'] = '') => {
      const field = getValues(`${fieldName}.${index}`);

      update(index, { ...field, archived: true, replaceId });

      // TR-4607: should switch on `Show archived` first time if no archived fields were before
      if (!archivedFields.length && autoSwitchedArchivedOn && !autoSwitchedArchivedOn?.current) {
        setShowArchived?.(true);
        autoSwitchedArchivedOn.current = true;
      }
    },
    [archivedFields.length, autoSwitchedArchivedOn, fieldName, getValues, setShowArchived, update],
  );

  const handleDeleteStatus = useCallback(
    (index: number) => {
      if (fields.length > 1) {
        remove(index);
      } else {
        replace([]);
      }
    },
    [fields.length, remove, replace],
  );

  const handleArchiveStatus = useCallback((index: number) => {
    setArchivingFieldIndex(index);
  }, []);

  const handleReplaceStatus = useCallback(
    (index: number) => (replaceId: SchoolProperty['id']) => {
      archiveStatus(index, replaceId);
      setArchivingFieldIndex(undefined);
    },
    [archiveStatus],
  );

  const handleArchiveStatusWithoutReplacing = useCallback(
    (index: number) => () => {
      archiveStatus(index);
      setArchivingFieldIndex(undefined);
    },
    [archiveStatus],
  );

  const handleRestoreStatus = useCallback(
    (index: number) => async () => {
      const field = getValues(`${fieldName}.${index}`);
      // can not just use `update` as need to move to the end
      remove(index);
      append({ ...field, archived: false });
    },
    [append, getValues, fieldName, remove],
  );

  const handleDragEnd = useCallback<OnDragEndResponder>(
    (result) => {
      // dropped outside the list
      if (!result.destination) {
        return;
      }

      move(result.source.index, result.destination.index);
    },
    [move],
  );

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId={`StatusCategoryDroppable-${category}`}>
          {(provided) => (
            <Stack gap={2}>
              {showTitle && (
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="h4">{title}</Typography>
                  <Tooltip
                    componentsProps={{ tooltip: { sx: { p: 1.25 } } }}
                    title={formatMessage({
                      id: `school-tabs-Statuses-CategoryDescription-${category.name}`,
                    })}
                  >
                    <IconButton inverse sx={{ '&.MuiButtonBase-root': { cursor: 'default' } }}>
                      <InformationIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
              <Stack
                {...provided.droppableProps}
                ref={provided.innerRef}
                alignItems="flex-start"
                gap={2}
              >
                {activeFields.length > 0 && (
                  <Stack gap={1}>
                    {activeFields.map(({ field, index }, arrayIndex) => {
                      return (
                        <Draggable key={field.id} draggableId={field.id} index={index}>
                          {(provided) => (
                            <Stack
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              direction="row"
                              alignItems="center"
                              gap={1}
                              sx={{ width: '100%', bgcolor: 'background.default' }}
                              style={provided.draggableProps.style}
                            >
                              {activeFields.length > 1 && (
                                <IconButton inverse {...provided.dragHandleProps}>
                                  <DragIcon />
                                </IconButton>
                              )}
                              <ControlTextField
                                name={`${fieldName}.${index}.name`}
                                control={control}
                                rules={{
                                  required: true,
                                  validate: (value, formValues) => {
                                    // Check for name uniqueness
                                    for (let i = 0; i < formValues[type].length; i++) {
                                      if (i === index) {
                                        continue;
                                      }

                                      const field = formValues[type][i];

                                      if (
                                        field.name?.trim().toLowerCase() ===
                                        `${value ?? ''}`.trim().toLowerCase()
                                      ) {
                                        return formatMessage({
                                          id: field.archived
                                            ? 'school-tabs-Statuses-ArchivedExists'
                                            : 'school-tabs-Statuses-ActiveExists',
                                        });
                                      }
                                    }

                                    return true;
                                  },
                                }}
                                customIcon={
                                  getCategoryTypeFromName(category.name) === 'former' &&
                                  field.originId &&
                                  field.originId === leavingStatusId && (
                                    <Tooltip
                                      PopperProps={{
                                        ref: tooltipPopper,
                                      }}
                                      title={
                                        <Stack gap={1}>
                                          {formatMessage({
                                            id: 'school-tabs-LeavingReasons-FormerTooltip',
                                          })}
                                          {pathname === '/settings/statuses' && (
                                            <Stack
                                              sx={{
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                gap: 1,
                                                cursor: 'pointer',
                                                pointerEvents: 'auto',
                                              }}
                                              onClick={() => {
                                                trackAmplitudeEvent({
                                                  name: AnalyticsEventName.RLOpen,
                                                  payload: { user_role: permissions },
                                                });
                                                navigate('/settings/rollover_and_leaving');
                                              }}
                                            >
                                              <Icon>
                                                <SettingsIcon />
                                              </Icon>

                                              <Typography>
                                                <FormattedMessage id="school-tabs-LeavingReasons-ChangeFormerStatus" />
                                              </Typography>
                                            </Stack>
                                          )}
                                        </Stack>
                                      }
                                      open={isFormerStatusTooltipVisible}
                                      onMouseEnter={showFormerStatusTooltip}
                                    >
                                      <IconButton>
                                        <LeavingReasonIcon />
                                      </IconButton>
                                    </Tooltip>
                                  )
                                }
                                label={formatMessage({ id: 'school-tabs-Statuses-StatusName' })}
                                autoFocus={
                                  shouldFocusLastItem && arrayIndex === activeFields.length - 1
                                }
                                solid
                                fullWidth
                                hideLabel
                              />

                              {setShowArchived ? (
                                activeFields.length > 0 && (
                                  <SchoolGeneralRowAction
                                    id={field.originId}
                                    index={index}
                                    isLocked={
                                      field.conduct_default ||
                                      field.group_default ||
                                      field.student_default ||
                                      field.staff_default ||
                                      field.re_enrollment_default
                                    }
                                    lockMessage={formatMessage({
                                      id: field.re_enrollment_default
                                        ? 'school-tabs-Statuses-Tooltip-ReEnrollmentDefault'
                                        : 'school-tabs-Statuses-Tooltip-DefaultFilter',
                                    })}
                                    onArchive={handleArchiveStatus}
                                    onDelete={handleDeleteStatus}
                                  />
                                )
                              ) : (
                                <>
                                  {!field.originId && (
                                    <IconButton inverse onClick={() => handleDeleteStatus(index)}>
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                                </>
                              )}
                            </Stack>
                          )}
                        </Draggable>
                      );
                    })}
                  </Stack>
                )}

                {provided.placeholder}

                <Button variant="text" startIcon={<PlusIcon />} onClick={addStatus} sx={{ py: 0 }}>
                  <FormattedMessage id="school-tabs-Statuses-AddStatus" />
                </Button>

                {showArchived &&
                  archivedFields.map(({ field, index }) => (
                    <Stack
                      key={field.id}
                      direction="row"
                      alignItems="center"
                      gap={1}
                      pl={3.5}
                      width="100%"
                    >
                      <TextField
                        value={field.name}
                        sx={{
                          '&&& .MuiOutlinedInput-notchedOutline': { borderColor: 'common.light3' },
                        }}
                        fullWidth
                        disabled
                        label={formatMessage({ id: 'school-tabs-Statuses-StatusName' })}
                      />

                      <IconButton onClick={handleRestoreStatus(index)}>
                        <RollBackIcon />
                      </IconButton>
                    </Stack>
                  ))}
              </Stack>
            </Stack>
          )}
        </Droppable>
      </DragDropContext>

      {archivingFieldIndex != null && (
        <SchoolGeneralStatusesReplaceModal
          group={title}
          field={fields[archivingFieldIndex]}
          fields={activeFields.map(({ field }) => field)}
          onClose={handleCloseReplaceModal}
          onReplace={handleReplaceStatus(archivingFieldIndex)}
          onArchiveWithoutReplacing={handleArchiveStatusWithoutReplacing(archivingFieldIndex)}
        />
      )}
    </>
  );
};

type SchoolTuneStatusesModalCategoryBlockProps = {
  category: SchoolPropertyCategory;
  subCategories?: SchoolPropertyCategory[];
} & Omit<SchoolTuneStatusesModalCategoryProps, 'category' | 'fieldName' | 'title'>;

export const SchoolTuneStatusesModalCategoryBlock: FC<
  SchoolTuneStatusesModalCategoryBlockProps
> = ({ type, category, subCategories, ...props }) => {
  const { formatMessage } = useIntl();
  const hasSubCategories = !!subCategories?.length;
  const categories = hasSubCategories ? sortByOrder(subCategories) : [category];

  return (
    <Stack gap={1} height="100%">
      <Typography variant="h4">
        {formatMessage({ id: `school-tabs-Statuses-Category-${category.name}` })}
      </Typography>
      <Paper
        elevation={0}
        sx={{ flex: '1 1 100%', p: 1, bgcolor: 'background.default', overflowY: 'auto' }}
      >
        <Stack gap={2}>
          {categories.map((category) => (
            <SchoolTuneStatusesModalCategory
              key={category.id}
              type={type}
              category={category}
              title={formatMessage({ id: `school-tabs-Statuses-Category-${category.name}` })}
              {...props}
            />
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
};
